import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext({
  permissions: {},
  loading: true,
  hasPermission: () => false,
  refreshPermissions: () => {},
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
  const { api, isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const response = await api().get('/user/permissions');
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Set default permissions based on role if API fails
      setPermissions(getDefaultPermissionsForRole(user?.role));
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated, user?.role]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!permissions || typeof permissions !== 'object') return false;
    return permissions[permission] === true;
  }, [permissions]);

  // Refresh permissions (e.g., after admin updates)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionsContext.Provider value={{ 
      permissions, 
      loading, 
      hasPermission, 
      refreshPermissions 
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Default permissions for each role (fallback if API fails)
const getDefaultPermissionsForRole = (role) => {
  const defaults = {
    super_admin: {
      view_simulations: true,
      create_simulation: true,
      edit_simulation: true,
      delete_simulation: true,
      export_simulation: true,
      view_all_simulations: true,
      make_decision: true,
      manage_users: true,
      manage_permissions: true,
      view_analytics: true,
      manage_clusters: true,
      manage_recipients: true,
      send_to_hg: true,
      change_business_status: true,
      view_audit_trail: true,
    },
    admin: {
      view_simulations: true,
      create_simulation: true,
      edit_simulation: true,
      delete_simulation: true,
      export_simulation: true,
      view_all_simulations: true,
      make_decision: true,
      manage_users: false,
      manage_permissions: false,
      view_analytics: true,
      manage_clusters: true,
      manage_recipients: true,
      send_to_hg: true,
      change_business_status: true,
      view_audit_trail: true,
    },
    commercial: {
      view_simulations: true,
      create_simulation: true,
      edit_simulation: true,
      delete_simulation: false,
      export_simulation: true,
      view_all_simulations: false,
      make_decision: false,
      manage_users: false,
      manage_permissions: false,
      view_analytics: false,
      manage_clusters: false,
      manage_recipients: false,
      send_to_hg: false,
      change_business_status: false,
      view_audit_trail: false,
    },
    readonly: {
      view_simulations: true,
      create_simulation: false,
      edit_simulation: false,
      delete_simulation: false,
      export_simulation: true,
      view_all_simulations: false,
      make_decision: false,
      manage_users: false,
      manage_permissions: false,
      view_analytics: false,
      manage_clusters: false,
      manage_recipients: false,
      send_to_hg: false,
      change_business_status: false,
      view_audit_trail: false,
    },
    prestataire: {
      view_simulations: false,
      create_simulation: false,
      edit_simulation: false,
      delete_simulation: false,
      export_simulation: false,
      view_all_simulations: false,
      make_decision: false,
      manage_users: false,
      manage_permissions: false,
      view_analytics: false,
      manage_clusters: false,
      manage_recipients: false,
      send_to_hg: false,
      change_business_status: false,
      view_audit_trail: false,
      view_chantiers: true,
      create_chantier: true,
      edit_chantier: true,
    },
  };
  
  return defaults[role] || defaults.readonly;
};

export default PermissionsContext;
